<script setup lang="ts">
  import { computed } from "vue";
  import { Button } from "@/components/ui/button";
  import { Label } from "@/components/ui/label";
  import { Textarea } from "@/components/ui/textarea";

  interface Props {
    label?: string;
    id?: string;
    placeholder?: string;
    minHeight?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    label: "JSON",
    id: undefined,
    placeholder: '{"key": "value"}',
    minHeight: "100px",
  });

  const modelValue = defineModel<string>({ default: "" });

  const isValid = computed(() => {
    if (!modelValue.value || modelValue.value.trim() === "") {
      return true;
    }
    try {
      JSON.parse(modelValue.value);
      return true;
    } catch {
      return false;
    }
  });

  const formatJson = () => {
    if (!modelValue.value) return;
    try {
      const parsed = JSON.parse(modelValue.value);
      modelValue.value = JSON.stringify(parsed, null, 2);
    } catch {
      // JSON invalide, ne rien faire
    }
  };

  defineExpose({
    isValid,
  });
</script>

<template>
  <div class="grid gap-2">
    <div class="flex items-center justify-between">
      <Label
        :for="props.id"
        class="text-sm">
        {{ props.label }}
      </Label>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        class="text-xs h-6 px-2"
        :disabled="!isValid || !modelValue"
        @click="formatJson">
        Format JSON
      </Button>
    </div>
    <Textarea
      :id="props.id"
      v-model="modelValue"
      :placeholder="props.placeholder"
      :class="['text-sm font-mono resize-none', !isValid ? 'border-status-error focus-visible:ring-status-error' : '']"
      :style="{ minHeight: props.minHeight, maxHeight: '200px' }" />
    <p
      v-if="!isValid"
      class="text-xs text-status-error">
      Invalid JSON format
    </p>
  </div>
</template>
